import { useCallback, useRef, useState } from "react";

export const DEFAULT_UNDO_MAX_HISTORY = 50;

/** Phase 2: add `useDebouncedUndoPush` to coalesce rapid slider-driven snapshots on procedural tools. */

export interface UndoRedoApi<T> {
    push: (snapshot: T) => void;
    undo: (current: T, apply: (snap: T) => void) => void;
    redo: (current: T, apply: (snap: T) => void) => void;
    clear: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

export interface UseUndoRedoOptions<T> {
    maxHistory?: number;
    clone?: (value: T) => T;
    equals?: (a: T, b: T) => boolean;
}

export function useUndoRedo<T>({
    maxHistory = DEFAULT_UNDO_MAX_HISTORY,
    clone,
    equals,
}: UseUndoRedoOptions<T> = {}): UndoRedoApi<T> {
    const undoStack = useRef<T[]>([]);
    const redoStack = useRef<T[]>([]);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const push = useCallback(
        (snapshot: T) => {
            const entry = clone ? clone(snapshot) : snapshot;
            const top = undoStack.current[undoStack.current.length - 1];
            if (top !== undefined && equals?.(top, entry)) return;

            undoStack.current.push(entry);
            if (undoStack.current.length > maxHistory) {
                undoStack.current.shift();
            }
            if (redoStack.current.length > 0) {
                redoStack.current = [];
                setCanRedo(false);
            }
            setCanUndo(true);
        },
        [clone, equals, maxHistory],
    );

    const clear = useCallback(() => {
        undoStack.current = [];
        redoStack.current = [];
        setCanUndo(false);
        setCanRedo(false);
    }, []);

    const undo = useCallback(
        (current: T, apply: (snap: T) => void) => {
            const prev = undoStack.current.pop();
            if (prev === undefined) return;

            const cur = clone ? clone(current) : current;
            redoStack.current.push(cur);
            if (redoStack.current.length > maxHistory) {
                redoStack.current.shift();
            }

            apply(clone ? clone(prev) : prev);
            setCanUndo(undoStack.current.length > 0);
            setCanRedo(true);
        },
        [clone, maxHistory],
    );

    const redo = useCallback(
        (current: T, apply: (snap: T) => void) => {
            const next = redoStack.current.pop();
            if (next === undefined) return;

            const cur = clone ? clone(current) : current;
            undoStack.current.push(cur);
            if (undoStack.current.length > maxHistory) {
                undoStack.current.shift();
            }

            apply(clone ? clone(next) : next);
            setCanRedo(redoStack.current.length > 0);
            setCanUndo(true);
        },
        [clone, maxHistory],
    );

    return { push, undo, redo, clear, canUndo, canRedo };
}
